import type { Agent, Persisted } from '@tunasong/schemas'
import { Avatar, getColorCSS } from '@tunasong/ui-lib'
import type { ColorName } from '@tunasong/schemas'
import cn from 'classnames'
import type { FC, MouseEvent } from 'react'
import { resolveAgentVisuals } from './agent-visuals.js'
import styles from './agent-rail-card.module.css'
import { useAgentRuntimeStatus } from './use-agent-runtime-status.js'
import { deriveAgentUnread } from './use-agent-unread.js'

export interface AgentRailCardProps {
  agent: Persisted<Agent>
  /** The current user's per-agent read cursor for this agent (undefined = no cursor yet). */
  cursor?: string
  /** Whether this agent's panel is currently open. Drives the active-state styling. */
  active?: boolean
  onClick: (agent: Persisted<Agent>, event: MouseEvent<HTMLButtonElement>) => void
  className?: string
}

/**
 * Single avatar card in the Agent Rail.
 *
 * Renders the agent's avatar (initial fallback when no image) plus up to two
 * status indicators stacked at the corners:
 *
 *   - thinking → a pulsing spinner ring around the avatar.
 *   - error → red ringed dot, top-right (sticks until acknowledged).
 *   - unread → solid primary-color dot, bottom-right.
 *
 * Error and unread coexist (different corners): the user shouldn't lose
 * sight of pending unread state when an error fires. Error is
 * shape-differentiated from unread (ringed vs solid) so the cue isn't
 * color-only (WCAG 1.4.1).
 *
 * Click → caller decides (typically: opens the chat side-panel for this agent;
 * the side panel implementation lives under Joni's next UX sub-task).
 */
export const AgentRailCard: FC<AgentRailCardProps> = ({ agent, cursor, active, onClick, className }) => {
  const runtimeStatus = useAgentRuntimeStatus(agent)
  const unread = deriveAgentUnread(agent, cursor)
  const label = agent.name?.trim() || agent.roleLine?.trim() || 'Agent'
  const { color, Icon } = resolveAgentVisuals(agent)
  const backgroundColor = getColorCSS(color as ColorName)
  const statusBits: string[] = []
  if (runtimeStatus === 'error') {
    statusBits.push(agent.runtimeError?.trim() || 'error')
  }
  if (runtimeStatus === 'thinking') {
    statusBits.push('thinking')
  }
  if (unread) {
    statusBits.push('unread')
  }
  const ariaLabel = statusBits.length > 0 ? `${label} — ${statusBits.join(', ')}` : label
  return (
    <button
      type="button"
      className={cn(styles.card, className)}
      data-active={active ? 'true' : undefined}
      aria-label={ariaLabel}
      onClick={event => onClick(agent, event)}
    >
      <Avatar size="small" alt={label} style={{ backgroundColor, color: 'var(--tuna-content-on-color, #fff)' }}>
        <Icon className={styles.icon} aria-hidden="true" />
      </Avatar>
      {runtimeStatus === 'thinking' ? <span className={styles.thinking} aria-hidden="true" /> : null}
      {runtimeStatus === 'error' ? (
        <span className={cn(styles.indicator, styles['indicator--error'])} aria-hidden="true" />
      ) : null}
      {unread ? (
        <span className={cn(styles.indicator, styles['indicator--unread'])} aria-hidden="true" />
      ) : null}
    </button>
  )
}
